import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import 'date-fns';
import DateFnsUtils from '@date-io/date-fns';
import AloModal from '../../_shared/ui-components/YogaUI/alo-ui/AloModal';
import { heapTrack, IS_NATIVE_APP_CLIENT } from '../../../utils/helpers';
import { getLoyaltyBlockedStatus } from '../../_shared/loyaltyBlockedStatus';
import { isToday, isBefore } from 'date-fns';
import formatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import { MUI_MOBILE_BREAKPOINT } from '../../../utils/constants';
import { aloApiAuthFetch } from 'src/scripts/utils/alo-api-fetch';
import { InitialUrlSearchParams } from '../../../utils/environment';
import { openSignInModal } from '../../_shared/ui-components/SignInModal/utils';
import { useMediaQuery } from 'usehooks-ts';

const urlParams = InitialUrlSearchParams;

const BirthdayPicker = () => {
  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT);
  const [customerInfo, setCustomerInfo] = useState<{ birthday: Date; isCustomer?: boolean } | null>(null);
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(new Date());
  const [isUserBlocked, setUserBlockedStatus] = useState(false);
  const [forcedOpen, setForcedOpen] = useState(false);

  useEffect(() => {
    if (window.getCustomerInfo) {
      const { birthday, isCustomer } = window.getCustomerInfo();
      setCustomerInfo({ birthday, isCustomer });

      const { birthdayDeepLinkingEnabled } = window.getSiteSettings();
      const hasForceBirthdayInputParam = urlParams.get('force_birthday_input');
      if (birthdayDeepLinkingEnabled && hasForceBirthdayInputParam) {
        if (isCustomer) {
          if (!birthday) setForcedOpen(true);
        } else {
          if (!IS_NATIVE_APP_CLIENT) openSignInModal({ returnUrl: window.location.href });
        }
      }
    }
    const getUserStatus = async () => {
      setUserBlockedStatus(await getLoyaltyBlockedStatus());
    };
    getUserStatus();
  }, []);

  const handleDateChange = (date: Date | null) => {
    if (!date) return;
    const alignedWithLLdate = new Date(date.setHours(9)); // LL adds time (9:00:00). We add here to align with LL's MF to ensure proper day-to-next-birthday calculation.
    setSelectedDate(alignedWithLLdate);
  };

  const saveBirthday = () => {
    if (selectedDate) {
      const birthdayData: BirthdayData = {
        year: selectedDate.getFullYear(),
        day: selectedDate.getDate(),
        month: selectedDate.getMonth() + 1,
      };
      updateBirthday(birthdayData).then((res) => {
        if (res.status === 200 && res.ok) {
          // [REVISIT] Should we show countdown before server response? Optimistic UI?
          const newCustomerInfo = { ...customerInfo, birthday: selectedDate };

          setCustomerInfo(newCustomerInfo); // [REVISIT] Add error state
          heapTrack(`${IS_NATIVE_APP_CLIENT ? 'Native App' : 'Web'} - Loyalty Page: Add Birthday`, {});
        }
      });
    }
  };

  const daysToNextBirthday = customerInfo?.birthday ? calculateDaysToNextBirthday(customerInfo?.birthday) : '';

  return (
    <>
      <p className="birthday-title">
        WE NEVER FORGET <br /> A BIRTHDAY
      </p>
      <p className="birthday-message">
        {customerInfo?.isCustomer ? (
          customerInfo?.birthday ? (
            <>
              The countdown is on. When your big day comes, {isMobile && <br />}
              we'll have a {!isMobile && <br />} special gift waiting in your bag.
            </>
          ) : (
            <>
              Add your birthday to receive a special gift during your
              <br />
              birthday month.
            </>
          )
        ) : (
          <>
            Join now—we’ve got a gift you’ll love {isMobile && <br />}
            all ready to go.
          </>
        )}
      </p>
      <div className="birthday-counter-section">
        {customerInfo?.isCustomer && (
          <AloModal
            wrapperClass="loyalty-birthday-modal"
            closeElement='[data-close="true"]'
            trigger={
              isUserBlocked ? null : (
                <button
                  disabled={!!customerInfo?.birthday}
                  type="button"
                  className={`birthday-button ${customerInfo?.birthday ? 'countdown' : 'add-birthday'}`}
                >
                  {customerInfo?.birthday ? daysToNextBirthday : 'Add your birthday'}
                </button>
              )
            }
            showCloseButton={true}
            open={forcedOpen}
          >
            <div className="modal-content">
              <p className="body">Share your birthday for a special gift during your birthday month.</p>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  margin="normal"
                  variant="inline"
                  id="date-picker-dialog"
                  label="Please pick your birthday"
                  format="MM/dd/yyyy"
                  value={selectedDate}
                  onChange={handleDateChange}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  InputProps={{ readOnly: true }}
                  disableFuture={true}
                  PopoverProps={{
                    className: 'birthdate-picker-popover-root',
                    disablePortal: true,
                  }}
                  allowKeyboardControl={true}
                  autoOk
                />
              </MuiPickersUtilsProvider>
              <button data-close="true" className="save-birthday btn" onClick={saveBirthday} disabled={!selectedDate}>
                Save Birthday
              </button>
            </div>
          </AloModal>
        )}
      </div>
    </>
  );
};

export default BirthdayPicker;

const renderBirthdayPicker = () => {
  const mountNode = document.querySelectorAll("[react-render-target='birthday-picker-modal']");
  if (mountNode.length > 0) {
    return mountNode.forEach((node) => ReactDOM.render(<BirthdayPicker />, node));
  } else {
    console.warn('missing render targets: birthday-picker-modal');
  }
};

export const InitBirthdayPicker = () => {
  renderBirthdayPicker();
};

function calculateDaysToNextBirthday(birthdayDateObject: Date) {
  const today = new Date();
  const clonedBirthdayDateObject = new Date(birthdayDateObject.getTime());

  //Updating year in the birthday object to current year
  const currentYearBirthday = new Date(clonedBirthdayDateObject.setFullYear(today.getFullYear()));

  // If the selected birthday is a future day(considering only date and month),upcomingBirthdayYear will be current year. otherwise next year.
  const upcomingBirthdayYear = isBefore(currentYearBirthday, today) ? today.getFullYear() + 1 : today.getFullYear();

  const daysToNextBirthday = formatDistanceToNowStrict(clonedBirthdayDateObject.setFullYear(upcomingBirthdayYear), {
    unit: 'day',
  });

  const isBirthdayToday = isToday(
    new Date(today.getFullYear(), birthdayDateObject.getMonth(), birthdayDateObject.getDate())
  );

  return isBirthdayToday ? '0 days' : daysToNextBirthday;
}

export const updateBirthday = async (birthdayData: BirthdayData) => {
  const response = await aloApiAuthFetch('/v1/account/birthday', {
    method: 'POST',
    body: JSON.stringify(birthdayData),
  });

  return { status: response.status, ok: response.ok, alreadyAdded: response.status === 409 };
};

interface BirthdayData {
  year: number;
  month: number;
  day: number;
}
